'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import Cookies from 'js-cookie';
import { AdblockDetector } from 'adblock-detector';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { PencilLineIcon } from '@phosphor-icons/react';
import AuthCard from './AuthCard';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import AuthLayout from './AuthLayout';
import JeeMainJanResultBanner from '../../public/Images/jee-main-2026-topper-banner.webp';

const useQuery = () => new URLSearchParams(useLocation().search);

const clearQuizrrParentDomainAuthCookie = () => {
  if (typeof document === 'undefined') return;
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const name = encodeURIComponent('token');
  document.cookie = `${name}=; Expires=${expires}; Max-Age=0; Path=/; Domain=.quizrr.in;`;
  document.cookie = `${name}=; Expires=${expires}; Max-Age=0; Path=/; Domain=quizrr.in;`;
};

const loginPromoBanner = (
  <img
    src={JeeMainJanResultBanner}
    alt="JEE Main 2026 January session — result and resources"
    className="w-full h-auto block"
    loading="eager"
  />
);

const Login = () => {
  const OTP_LIMIT = 3;
  const OTP_WINDOW_MS = 30 * 1000;

  localStorage.clear('userProfile');

  const navigate = useNavigate();
  const query = useQuery();
  const utmSource = query.get('utm_source');
  const utmMedium = query.get('utm_medium');
  const utmCampaign = query.get('utm_campaign');
  const [cookies, setCookie] = useCookies();

  const [step, setStep] = useState(1); //* 1 = send OTP, 2 = verify OTP
  const [loading, setLoading] = useState(false);

  const [otpChannel, setOtpChannel] = useState('sms');
  const [otp, setOtp] = useState('');
  const [otpRequestCount, setOtpRequestCount] = useState(0);
  const [otpWindowStart, setOtpWindowStart] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [showWhatsapp, setShowWhatsapp] = useState(true);

  const [formData, setFormData] = useState({
    mobile: '',
  });

  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAdBlocked, setIsAdBlocked] = useState(false);

  const mobileRef = useRef(null);

  const adbDetector = new AdblockDetector();
  const detected = adbDetector.detect();

  const redirect_url = query.get('redirect_url') || localStorage.getItem('redirect_url');

  useEffect(() => {
    if (!navigator.cookieEnabled || detected) {
      setIsAdBlocked(true);
    }
    axios.get('/api/platform').then((res) => {
      setShowWhatsapp(res?.data?.data?.showWhatsappSource);
    });
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  const validateMobile = (mobile) => /^[0-9]{10}$/.test(mobile);
  const validateOtp = (otp) => /^[0-9]{4,6}$/.test(otp);

  /* ---------------- STEP 1: SEND OTP ---------------- */
  const sendOtp = async (e) => {
    if (e) e.preventDefault();

    const trimmedMobile = formData.mobile.trim();
    const newErrors = {};

    if (!trimmedMobile) {
      newErrors.mobile = 'This field is required';
    } else if (!validateMobile(trimmedMobile)) {
      newErrors.mobile = 'Please enter a valid mobile number';
    }

    if (!otpChannel) {
      newErrors.otpChannel = 'This field is required';
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const now = Date.now();

    // Reset window if expired
    if (!otpWindowStart || now - otpWindowStart > OTP_WINDOW_MS) {
      setOtpWindowStart(now);
      setOtpRequestCount(0);
    }

    if (otpRequestCount >= OTP_LIMIT) {
      setAlertMessage('You can request OTP only 3 times in 30 seconds. Please wait.');
      setShowAlert(true);
      return;
    }

    setLoading(true);
    setShowAlert(false);

    try {
      await axios.post('/api/auth/user/sendOtp', {
        mobile: trimmedMobile,
        otpChannel,
        utmSource,
        utmMedium,
        utmCampaign,
      });

      setOtpRequestCount((prev) => prev + 1);
      setOtpWindowStart((prev) => prev || now);
      setResendTimer(30);
      setStep(2);
    } catch (err) {
      setAlertMessage(err?.response?.data?.message);
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- STEP 2: VERIFY OTP ---------------- */
  const verifyOtp = async (e) => {
    e.preventDefault();

    if (!validateOtp(otp)) {
      setErrors({ otp: 'Invalid OTP' });
      return;
    }

    setLoading(true);
    setShowAlert(false);

    try {
      const res = await axios.post('/api/auth/user/loginManual', {
        mobile: formData.mobile,
        otp,
        otpChannel,
      });

      const { token, user } = res.data.data;

      clearQuizrrParentDomainAuthCookie();
      setCookie('token', token, { path: '/', maxAge: 2592000 });
      Cookies.set('token', token, { path: '/', sameSite: 'None', secure: true });
      localStorage.setItem('userProfile', JSON.stringify(user));

      if (redirect_url) {
        window.location.href = redirect_url;
        localStorage.removeItem('redirect_url');
      } else {
        window.location.href = '/v2/home';
      }
    } catch (err) {
      setAlertMessage(err?.response?.data?.message);
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  if (isAdBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-[#ffffff] p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <h2 className="text-xl font-semibold flex flex-row items-center justify-center gap-2 mb-4 text-[#f3164e]">
            <AlertTriangle size={20} /> AdBlocker Detected
          </h2>
          <p className="mb-6">Please disable ad-blocker and enable cookies to continue.</p>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </div>
      </div>
    );
  }

  const isValidMobile = formData.mobile && validateMobile(formData.mobile);

  return (
    <AuthLayout promoBanner={loginPromoBanner}>
      <AuthCard title="Continue for AYJR" subtitle="Please login to continue">
        {/* ---------------- STEP 1 ---------------- */}
        {step === 1 && (
          <form onSubmit={sendOtp} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="mobile" style={{ color: '#1d2933' }}>
                Mobile Number (without country code)
              </Label>
              <Input
                ref={mobileRef}
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={(e) => setFormData({ mobile: e.target.value })}
                placeholder="Mobile Number"
                className={`h-10 rounded-full ${errors.mobile ? 'border-[#f3164e]' : ''}`}
                style={{
                  backgroundColor: '#ffffff',
                  border: errors.mobile ? '1px solid #f3164e' : '1px solid #d9e6f2',
                  color: '#1d2933',
                }}
              />
              {errors.mobile && (
                <div className="flex items-center gap-1 text-sm text-[#f3164e]">
                  <AlertTriangle size={12} />
                  {errors.mobile}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label style={{ color: '#1d2933' }}>Receive OTP via</Label>
              <RadioGroup value={otpChannel} onValueChange={setOtpChannel} className="flex gap-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sms" id="sms" />
                  <Label htmlFor="sms" className="cursor-pointer">
                    SMS
                  </Label>
                </div>
                {showWhatsapp && (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="whatsapp" id="whatsapp" />
                    <Label htmlFor="whatsapp" className="cursor-pointer">
                      WhatsApp
                    </Label>
                  </div>
                )}
              </RadioGroup>
            </div>

            {showAlert && (
              <div className="flex items-center gap-2 justify-center text-sm text-[#f3164e]">
                <AlertTriangle size={20} />
                {alertMessage}
              </div>
            )}

            <Button
              type="submit"
              disabled={!isValidMobile || loading}
              className="w-full h-10 text-base bg-[#4338ca] hover:bg-[#4338ca] rounded-full"
              style={{
                backgroundColor: '#4338ca)',
                color: '#ffffff',
              }}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          </form>
        )}

        {/* ---------------- STEP 2 ---------------- */}
        {step === 2 && (
          <form onSubmit={verifyOtp} className="space-y-5">
            <div className="space-y-2">
              <Label style={{ color: '#1d2933' }}>Enter OTP</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                className="h-10 rounded-full"
                type="number"
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #d9e6f2',
                  color: '#1d2933',
                }}
              />
              {errors.otp && <div className="text-sm text-[#f3164e] text-center">{errors.otp}</div>}
            </div>

            {showAlert && (
              <div className="flex items-center gap-2 justify-center text-sm text-[#f3164e]">
                <AlertTriangle size={20} />
                {alertMessage}
              </div>
            )}

            {/* BUTTON GROUP */}
            <div className="space-y-2">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 text-base bg-[#4338ca] hover:bg-[#4338ca] rounded-full"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>

              {resendTimer > 0 ? (
                <div className="text-sm text-center text-[#60748f]">Resend OTP in {resendTimer}s</div>
              ) : (
                <Button
                  type="button"
                  onClick={sendOtp}
                  disabled={loading}
                  className="w-full h-10 text-base rounded-full"
                >
                  Resend OTP
                </Button>
              )}
            </div>

            <button
              type="button"
              className="cursor-pointer flex items-center gap-2 text-sm w-full text-[#60748f]"
              onClick={() => setStep(1)}
            >
              <PencilLineIcon /> Edit mobile number
            </button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
};

export default Login;
